import React, { useEffect, useState, useMemo } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import { Popover } from 'react-bootstrap';
import { Formik, Form, Field } from 'formik';
import { SHOWING_PROVIDERS_CONST } from '@bb/common';
import { formatDateStrWithTime, formatTimeStr, getTimezoneCode } from 'utils';
import Step3TimeList from './Step3TimeList';
import styles from './appointment_book.module.scss';
import { HOME_INSPECTION_TYPES } from '../../constants';

const MIN_BOOKING_DURATION_MINUTES = 15;

// Helper function to convert values to finite numbers
const toFiniteNumber = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// Normalize duration to fit within the interval
const normalizeDurationForInterval = (value, interval, mode) => {
  if (value == null) return undefined;
  if (mode === 'ceil') return Math.ceil(value / interval) * interval;
  if (mode === 'floor') return Math.floor(value / interval) * interval;
  return value;
};

// Determine duration type based on appointment type
const getDurationTypeForAppointment = appointmentType => {
  if (['Appraisal', 'Photographer'].includes(appointmentType)) {
    return 'appraisal';
  }
  if (['Home Inspection', ...HOME_INSPECTION_TYPES].includes(appointmentType)) {
    return 'inspection';
  }
  return 'showing';
};

// Get min/max duration bounds from backend configuration
const getDurationBoundsForType = (
  showingProviderAdditionalInfo,
  durationType,
  interval,
) => {
  if (!showingProviderAdditionalInfo) return {};

  const durationConfigRoot =
    showingProviderAdditionalInfo.appointmentLength ||
    showingProviderAdditionalInfo.appointmentLengths ||
    showingProviderAdditionalInfo.length ||
    showingProviderAdditionalInfo.lengths ||
    showingProviderAdditionalInfo;

  if (!durationConfigRoot || typeof durationConfigRoot !== 'object') return {};

  const typeConfig =
    durationConfigRoot[durationType] ||
    durationConfigRoot[durationType.toUpperCase()] ||
    durationConfigRoot[`${durationType}Length`] ||
    {};

  // Capitalize first letter for backend key pattern (e.g., 'inspection' -> 'Inspection')
  const capitalizedType =
    durationType.charAt(0).toUpperCase() + durationType.slice(1);

  const minKey = `min${capitalizedType}LengthMinutes`;
  const maxKey = `max${capitalizedType}LengthMinutes`;

  const minDuration = toFiniteNumber(
    typeConfig.minLength ??
      typeConfig.min ??
      typeConfig.minDuration ??
      typeConfig.minDurationMinutes ??
      durationConfigRoot[minKey] ??
      durationConfigRoot[`${durationType}MinLength`] ??
      durationConfigRoot[`${durationType}Min`],
  );

  const maxDuration = toFiniteNumber(
    typeConfig.maxLength ??
      typeConfig.max ??
      typeConfig.maxDuration ??
      typeConfig.maxDurationMinutes ??
      durationConfigRoot[maxKey] ??
      durationConfigRoot[`${durationType}MaxLength`] ??
      durationConfigRoot[`${durationType}Max`],
  );

  const normalizedMin = normalizeDurationForInterval(
    minDuration,
    interval,
    'ceil',
  );
  const normalizedMax = normalizeDurationForInterval(
    maxDuration,
    interval,
    'floor',
  );

  return {
    minDuration: normalizedMin,
    maxDuration: normalizedMax,
  };
};

const Step3Time = ({
  listing,
  extAuth,
  isListingContact,
  isTimeChange,
  originalAppointment,
  appointment,
  appointmentsInfo = {},
  showingProviderAdditionalInfo,
  duration,
  autoConfirm,
  availableTimes,
  isMobile,
  isAdmin,
  isTeamAdmin,
  isListingAgent,
  invalidTimeslot,
  customDuration,
  dateNotAvailable,
  selectedTimes,
  enableOpenHouses,
  openHouses,
  openHouseTimes,
  offerDate,
  offerAnytime,
  timesOfDay,
  hiddenTimes,
  maxDuration,
  minutes,
  hours,
  currentStep,
  mobileWorkflowSteps,
  loadingTimes,
  appointmentTimes,
  showCaptcha,
  partialAutoConfirm,
  interval = 15,
  // functions
  expandTime,
  setDuration,
  setCustomDuration,
  updateState,
  selectTime,
  moveToStep,
  hasValidTime,
  getPrettyDate,
  timePassed,
  enableCustomDuration,
}) => {
  const [showCustomDuration, setShowCustomDuration] = useState(false);
  const [filtMinutes, setFiltMinutes] = useState(minutes);

  const getTimeZone = () => {
    // Get timezone region (EST/EDT) from selected time or date
    const time = new Date(appointment.time || appointment.date);

    const timezone = getTimezoneCode(listing?.timezone, time);
    return timezone;
  };

  // External RETS/dashboard booking can have provider bounds without listing.showingProvider.
  const isExternalBookingFlow = Boolean(
    extAuth?.retsDashboard ||
    extAuth?.coopDashboard ||
    extAuth?.externalDashboard ||
    extAuth?.retsId ||
    extAuth?.dashboardId,
  );
  const hasNonSupraProvider =
    Boolean(listing?.showingProvider) &&
    listing.showingProvider !== SHOWING_PROVIDERS_CONST.SUPRA;
  const isExternalProvider =
    hasNonSupraProvider ||
    (isExternalBookingFlow && Boolean(showingProviderAdditionalInfo));
  const externalProviderInfo = isExternalProvider
    ? showingProviderAdditionalInfo
    : undefined;

  // Calculate effective min/max duration from backend configuration
  const effectiveDurationBounds = useMemo(() => {
    const durationType = getDurationTypeForAppointment(appointment.type);
    const typeDurationBounds = getDurationBoundsForType(
      externalProviderInfo,
      durationType,
      interval,
    );

    let effectiveMinDuration;
    if (Number.isFinite(typeDurationBounds.minDuration)) {
      effectiveMinDuration = typeDurationBounds.minDuration;
    } else {
      effectiveMinDuration = MIN_BOOKING_DURATION_MINUTES;
    }

    let effectiveMaxDuration;
    if (Number.isFinite(typeDurationBounds.maxDuration)) {
      effectiveMaxDuration = typeDurationBounds.maxDuration;
    } else {
      effectiveMaxDuration = maxDuration;
    }

    return {
      minDuration: effectiveMinDuration,
      maxDuration: effectiveMaxDuration,
    };
  }, [externalProviderInfo, appointment.type, interval, maxDuration]);

  // Admins and listing agents can select a duration that exceeds the max duration configuration
  // Home inspections and appraisals may also exceed the max duration configuration
  // However, when external showing provider is involved, strictly enforce their max duration
  const allowExceedMaxDuration =
    !isExternalProvider &&
    (isAdmin ||
      isListingAgent ||
      [
        ...HOME_INSPECTION_TYPES,
        'Home Inspection',
        'Appraisal',
        'Photographer',
        'Pest Control',
      ].includes(appointment.type));

  // Changes to custom duration hour prevents some custom minutes selections if it would exceed max duration
  useEffect(() => {
    const effectiveMax = effectiveDurationBounds.maxDuration;
    const effectiveMin = effectiveDurationBounds.minDuration;
    const selectedHourInMinutes = Number(customDuration.durationHour) * 60;

    const newFilter = minutes.filter(minute => {
      const totalDuration = selectedHourInMinutes + minute;
      // Min duration is ALWAYS enforced
      if (totalDuration < effectiveMin) return false;
      // Max duration is only enforced when allowExceedMaxDuration is false
      if (!allowExceedMaxDuration && totalDuration > effectiveMax) return false;
      return true;
    });

    setFiltMinutes(newFilter);
    if (!newFilter.includes(customDuration.durationMinutes)) {
      const newMinutes = newFilter[newFilter.length - 1] ?? 0;
      updateState(draft => {
        draft.customDuration.durationMinutes = newMinutes;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allowExceedMaxDuration,
    customDuration.durationHour,
    customDuration.durationMinutes,
    hours,
    effectiveDurationBounds.maxDuration,
    effectiveDurationBounds.minDuration,
    minutes,
    // updateState intentionally omitted - passed from parent, stable reference
  ]);

  // for time selection
  useEffect(() => {
    if (customDuration.enabled) {
      setCustomDuration();
    } else if (appointment.time) {
      setDuration(duration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customDuration.durationMinutes,
    customDuration.durationHour,
    appointment.time,
    duration,
    // customDuration.enabled, setCustomDuration, setDuration intentionally omitted - stable functions from parent
  ]);

  useEffect(() => {
    // Show custom duration if max > 60 OR if user has privilege to exceed max (admins/listing agents)
    if (effectiveDurationBounds.maxDuration > 60 || allowExceedMaxDuration) {
      setShowCustomDuration(true);
    } else {
      setShowCustomDuration(false);
      enableCustomDuration(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDurationBounds.maxDuration, allowExceedMaxDuration]);
  // enableCustomDuration intentionally omitted - stable function from parent

  const effectiveMax = effectiveDurationBounds.maxDuration;
  const effectiveMin = effectiveDurationBounds.minDuration;

  // Reset standard/custom duration when appointment type changes if it no longer satisfies the new bounds
  useEffect(() => {
    const isDurationValidForBounds = totalDuration => {
      if (totalDuration < effectiveMin) return false;
      if (!allowExceedMaxDuration && totalDuration > effectiveMax) {
        return false;
      }
      return true;
    };

    const nextStandardDuration = [15, 30, 45, 60].find(
      isDurationValidForBounds,
    );

    if (customDuration.enabled) {
      const currentCustomDuration =
        Number(customDuration.durationHour) * 60 +
        Number(customDuration.durationMinutes);

      if (isDurationValidForBounds(currentCustomDuration)) return;

      const nextDuration = nextStandardDuration ?? effectiveMin;
      updateState(draft => {
        draft.customDuration.durationHour = Math.floor(nextDuration / 60);
        draft.customDuration.durationMinutes = nextDuration % 60;
      });
      return;
    }

    if (isDurationValidForBounds(duration)) return;

    updateState(draft => {
      draft.duration = nextStandardDuration ?? effectiveMin;
    });
  }, [
    appointment.type,
    customDuration.enabled,
    customDuration.durationHour,
    customDuration.durationMinutes,
    duration,
    effectiveMin,
    effectiveMax,
    allowExceedMaxDuration,
    updateState,
  ]);

  const validate = values => {
    const errors = {};

    // Validate custom duration meets requirements
    if (customDuration.enabled) {
      const totalDuration =
        Number(values.durationHours) * 60 + Number(values.durationMinutes);
      // Min duration is ALWAYS enforced
      if (totalDuration < effectiveMin) {
        errors.durationMinutes = `Duration must be at least ${effectiveMin} minutes`;
      }
      // Max duration is only enforced when allowExceedMaxDuration is false
      if (!allowExceedMaxDuration && totalDuration > effectiveMax) {
        errors.durationMinutes = `Duration must not exceed ${effectiveMax} minutes`;
      }
    }

    updateState(draft => {
      draft.duration = Number(values.duration);
      draft.customDuration.durationHour = Number(values.durationHours);
      draft.customDuration.durationMinutes = Number(values.durationMinutes);
    });
    return errors;
  };

  // Generate full hours array based on effectiveMax (don't rely on limited hours prop)
  const maxPossibleHours = allowExceedMaxDuration
    ? Math.max(5, Math.ceil(effectiveMax / 60)) // Allow at least 5 hours for privileged users
    : Math.ceil(effectiveMax / 60);
  const allHours = Array.from({ length: maxPossibleHours + 1 }, (_, i) => i);

  const filtHours = allHours.filter(hour => {
    const hourInMinutes = hour * 60;
    // Hour is valid if there exists at least one valid minute value
    // that makes total duration within bounds
    const hasValidMinute = minutes.some(minute => {
      const totalDuration = hourInMinutes + minute;
      // Min duration is ALWAYS enforced
      if (totalDuration < effectiveMin) return false;
      // Max duration is only enforced when allowExceedMaxDuration is false
      if (!allowExceedMaxDuration && totalDuration > effectiveMax) return false;
      return true;
    });
    return hasValidMinute;
  });

  const maxDurationHours = Math.floor(effectiveMax / 60);
  const maxDurationMinutes = effectiveMax % 60;
  let maxDurationTitle;
  if (maxDurationHours === 0) {
    maxDurationTitle = `${maxDurationMinutes} min `;
  } else if (maxDurationMinutes === 0) {
    maxDurationTitle = `${maxDurationHours} hr`;
  } else {
    maxDurationTitle = `${maxDurationHours} hr ${maxDurationMinutes} min`;
  }

  const PopoverComponent = (
    <Popover
      id="popover-positioned-left"
      title={<b>Duration (max {maxDurationTitle})</b>}
    >
      <Formik
        initialValues={{
          duration,
          durationHours: customDuration.durationHour || 1,
          durationMinutes: customDuration.durationMinutes || 0,
        }}
        validate={values => validate(values)}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);
        }}
      >
        {({ values }) => (
          <Form>
            {customDuration && !customDuration.enabled && (
              <div>
                {effectiveDurationBounds.minDuration <= 15 && (
                  <div className="radio">
                    <TimeLabel>
                      <Field
                        type="radio"
                        value={15}
                        checked={Number.parseInt(values.duration, 10) === 15}
                        name="duration"
                        disabled={showCaptcha}
                      />
                      15 minutes
                    </TimeLabel>
                  </div>
                )}
                {effectiveDurationBounds.minDuration <= 30 &&
                  (allowExceedMaxDuration ||
                    effectiveDurationBounds.maxDuration >= 30) && (
                    <div className="radio">
                      <TimeLabel
                        isRed={effectiveDurationBounds.maxDuration < 30}
                      >
                        <Field
                          type="radio"
                          name="duration"
                          checked={Number.parseInt(values.duration, 10) === 30}
                          value={30}
                          disabled={showCaptcha}
                        />
                        30 minutes
                      </TimeLabel>
                    </div>
                  )}
                {effectiveDurationBounds.minDuration <= 45 &&
                  (allowExceedMaxDuration ||
                    effectiveDurationBounds.maxDuration >= 45) && (
                    <div className="radio">
                      <TimeLabel
                        isRed={effectiveDurationBounds.maxDuration < 45}
                      >
                        <Field
                          type="radio"
                          name="duration"
                          checked={Number.parseInt(values.duration, 10) === 45}
                          value={45}
                          disabled={showCaptcha}
                        />
                        45 minutes
                      </TimeLabel>
                    </div>
                  )}
                {effectiveDurationBounds.minDuration <= 60 &&
                  (allowExceedMaxDuration ||
                    effectiveDurationBounds.maxDuration >= 60) && (
                    <div className="radio">
                      <TimeLabel
                        isRed={effectiveDurationBounds.maxDuration < 60}
                      >
                        <Field
                          type="radio"
                          name="duration"
                          checked={Number.parseInt(values.duration, 10) === 60}
                          value={60}
                          disabled={showCaptcha}
                        />
                        60 minutes
                      </TimeLabel>
                    </div>
                  )}
              </div>
            )}

            {customDuration?.enabled && (
              <>
                <FlexDiv>
                  <label>Hours:</label>
                  <Field
                    component="select"
                    className="form-control"
                    name="durationHours"
                  >
                    {filtHours?.map(hour => (
                      <option key={hour} value={hour}>
                        {hour}
                      </option>
                    ))}
                  </Field>
                </FlexDiv>
                <FlexDiv>
                  <label>Minutes:</label>
                  <Field
                    component="select"
                    className="form-control"
                    name="durationMinutes"
                  >
                    {filtMinutes?.map(minute => (
                      <option key={minute} value={minute}>
                        {minute}
                      </option>
                    ))}
                  </Field>
                </FlexDiv>
              </>
            )}

            {showCustomDuration && (
              <div style={{ textAlign: 'center' }}>
                {customDuration.enabled ? (
                  <a onClick={() => enableCustomDuration(false)}>
                    Standard Duration
                  </a>
                ) : (
                  <a onClick={() => enableCustomDuration(true)}>
                    Custom Duration
                  </a>
                )}
              </div>
            )}

            <div style={{ textAlign: 'center' }}>
              <hr style={{ margin: '10px 0px' }} />
              <button
                onClick={() => document.body.click()}
                type="submit"
                style={{
                  fontSize: '100%',
                  fontFamily: 'inherit',
                  border: 0,
                  padding: 0,
                }}
                data-cy="done-customize-duration"
              >
                Done
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Popover>
  );

  return (
    <div
      className={`col-md-3 ${
        currentStep === mobileWorkflowSteps.selectTimeStep
          ? styles.stepActive
          : styles.stepInactive
      }`}
    >
      <hr className={`${styles.titleDivider} ${styles.titleDividerTop}`} />

      <h3 className={styles.titleBlock}>
        <span className={styles.stepNumber}>Step 3 - </span>
        <span className={styles.stepTitle}>
          {isMobile && (
            <span>
              <strong
                onClick={() => moveToStep(1)}
                style={{ color: '#3498db' }}
              >
                Date
              </strong>{' '}
              /{' '}
            </span>
          )}
          Select Time
        </span>
        {isMobile && isListingContact && isTimeChange && (
          <div className={styles.orignalTimeSubTitle}>
            <strong>Original Request:</strong>{' '}
            {formatDateStrWithTime(originalAppointment.time, listing.timezone)}
            {' - '}
            {formatTimeStr(originalAppointment.timeEnd, listing.timezone)}
          </div>
        )}
      </h3>

      <hr className={`${styles.titleDivider} ${styles.titleDividerBottom}`} />
      {appointment.date && (
        <div>Times reflect the listing&apos;s timezone ({getTimeZone()})</div>
      )}
      <hr className={`${styles.titleDivider} ${styles.titleDividerBottom}`} />

      {hasValidTime() ? (
        <div className="row">
          <div
            className={`col-sm-12 col-sm-push-0 col-xs-5 col-xs-push-7 ${styles.mobileContent}`}
          >
            {/* mobile */}
            {!customDuration.enabled && isMobile && (
              <Formik
                initialValues={{ duration }}
                validate={values => {
                  updateState(draft => {
                    draft.duration = values.duration;
                  });
                }}
                onSubmit={(values, { setSubmitting }) => {
                  setSubmitting(false);
                }}
              >
                {({ values }) => (
                  <Form>
                    <div style={{ marginLeft: '-22.5px', textAlign: 'center' }}>
                      <label style={{ display: 'block', textAlign: 'center' }}>
                        Duration:
                      </label>
                      {effectiveDurationBounds.minDuration <= 15 && (
                        <div className="radio">
                          <label>
                            <Field
                              type="radio"
                              name="duration"
                              checked={
                                Number.parseInt(values.duration, 10) === 15
                              }
                              value={15}
                              disabled={showCaptcha}
                            />
                            15 minutes
                          </label>
                        </div>
                      )}
                      {effectiveDurationBounds.minDuration <= 30 &&
                        (allowExceedMaxDuration ||
                          effectiveDurationBounds.maxDuration >= 30) && (
                          <div className="radio">
                            <TimeLabel
                              isRed={effectiveDurationBounds.maxDuration < 30}
                            >
                              <Field
                                type="radio"
                                name="duration"
                                checked={
                                  Number.parseInt(values.duration, 10) === 30
                                }
                                value={30}
                                disabled={showCaptcha}
                              />
                              30 minutes
                            </TimeLabel>
                          </div>
                        )}
                      {effectiveDurationBounds.minDuration <= 60 &&
                        (allowExceedMaxDuration ||
                          effectiveDurationBounds.maxDuration >= 60) && (
                          <div className="radio">
                            <TimeLabel
                              isRed={effectiveDurationBounds.maxDuration < 60}
                            >
                              <Field
                                type="radio"
                                name="duration"
                                checked={
                                  Number.parseInt(values.duration, 10) === 60
                                }
                                value={60}
                                disabled={showCaptcha}
                              />
                              60 minutes
                            </TimeLabel>
                          </div>
                        )}
                    </div>
                  </Form>
                )}
              </Formik>
            )}

            {!appointment.recordAppointment && (
              <div>
                {invalidTimeslot && (
                  <label className="invalid-timeslot">
                    RESTRICTED TIME SELECTED
                  </label>
                )}
                {(listing.canEdit ||
                  listing.canRead ||
                  extAuth?.coopDashboard ||
                  extAuth?.retsDashboard) &&
                  !invalidTimeslot &&
                  appointment.date &&
                  appointment.time &&
                  (autoConfirm || partialAutoConfirm) && (
                    <label className="autoconfirm-color">
                      AUTO-CONFIRM TIME SELECTED
                    </label>
                  )}
              </div>
            )}
          </div>
          <div className="col-sm-12 col-sm-pull-0 col-xs-7 col-xs-pull-5">
            {!dateNotAvailable && (
              <div
                id="timeSelectorContainer"
                className={classNames(styles.timeSelector, {
                  'transition animated': isMobile,
                  fadeOut: loadingTimes && isMobile,
                  fadeIn: !loadingTimes && isMobile,
                })}
              >
                {timesOfDay?.map(time => (
                  <Step3TimeList
                    key={time}
                    isAdmin={isAdmin}
                    isListingAgent={isListingAgent}
                    isTeamAdmin={isTeamAdmin}
                    appointment={appointment}
                    time={time}
                    hiddenTimes={hiddenTimes}
                    timePassed={timePassed}
                    availableTimes={availableTimes}
                    appointmentsInfo={appointmentsInfo}
                    duration={duration}
                    maxDuration={effectiveMax}
                    openHouses={openHouses}
                    openHouseTimes={enableOpenHouses ? openHouseTimes : []}
                    offerDate={offerDate}
                    offerAnytime={offerAnytime}
                    selectedTimes={selectedTimes}
                    selectTime={selectTime}
                    showCaptcha={showCaptcha}
                    PopoverComponent={PopoverComponent}
                    customDuration={customDuration}
                    appointmentTimes={appointmentTimes}
                    enableCustomDuration={enableCustomDuration}
                    expandTime={expandTime}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-xs-12">
            <div
              className={`${styles.timeSelectorMessage} timeslot-unavailable`}
            >
              <h4>
                <i className="fa fa-frown-o" />
              </h4>
              <p>There are no available showing times on:</p>
              <p>{getPrettyDate(appointment.date)}</p>
              <p>Please select a different day.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FlexDiv = styled.div`
  width: 80%;
  margin: auto;
  display: flex;
  justify-content: space-between;
`;

const TimeLabel = styled.label`
  ${({ isRed }) => {
    if (isRed) {
      return `
    color: red;
    font-weight: bold;
    `;
    }
    return '';
  }};
`;

export default Step3Time;

// Export utility functions for testing
export {
  toFiniteNumber,
  normalizeDurationForInterval,
  getDurationTypeForAppointment,
  getDurationBoundsForType,
};
